/** @format */

import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import {
	UserPlus,
	Share2,
	Gift,
	ArrowRight,
	UserCheck,
	Copy,
} from "lucide-react"; // Refined Icons
import { useSelector } from "react-redux"; // Import useSelector

// --- Animation Variants ---
const sectionVariants = {
	hidden: { opacity: 0, y: 20 },
	visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const stepVariants = {
	hidden: { opacity: 0, scale: 0.8 },
	visible: (delay) => ({
		// Accept delay as custom prop
		opacity: 1,
		scale: 1,
		transition: { type: "spring", stiffness: 150, damping: 15, delay },
	}),
};

// --- Step Component ---
const StepCard = ({ icon: Icon, title, description, stepNumber, delay }) => (
	<motion.div
		variants={stepVariants}
		custom={delay} // Pass delay to variants
		initial="hidden"
		whileInView="visible"
		viewport={{ once: true, amount: 0.4 }}
		className="flex flex-col items-center text-center p-4"
	>
		<div className="relative mb-3">
			<div className="w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center border-2 border-primary/20">
				<Icon size={32} strokeWidth={1.5} />
			</div>
			{/* Step Number Badge */}
			<span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-secondary text-white flex items-center justify-center text-xs font-bold border-2 border-white">
				{stepNumber}
			</span>
		</div>
		<h3 className="font-semibold text-lg mb-1 text-gray-800">{title}</h3>
		<p className="text-sm text-gray-600">{description}</p>
	</motion.div>
);

// --- Main ReferFriend Screen ---
const ReferFriend = () => {
	const navigate = useNavigate();
	// Check login status to tailor CTAs
	const { userInfo, isGuest } = useSelector((state) => state.userLogin);
	const isLoggedIn = userInfo && !isGuest;

	// Simplified steps data
	const steps = [
		{
			icon: UserCheck,
			title: "Get Your Code",
			description:
				"Log in or sign up to find your unique referral code in your profile.",
			delay: 0.1,
		},
		{
			icon: Share2,
			title: "Share With Friends",
			description: "Send your code to friends who haven't shopped with us yet.",
			delay: 0.2,
		},
		{
			icon: Gift,
			title: "You Both Get Rewarded",
			description:
				"Your friend gets 10% off their first order, and you get store credit after their purchase!",
			delay: 0.3,
		}, // Specify reward type (e.g., $10 credit)
	];

	return (
		<>
			<Helmet>
				<title>Refer a Friend, Get Rewards | Off Cut Shades</title>
				<meta
					name="description"
					content="Share Off Cut Shades with friends! Give them 10% off their first custom shade order, and get store credit for yourself. Learn how our referral program works."
				/>
				<link rel="canonical" href="https://offcutshades.com/refer-a-friend" />
			</Helmet>

			{/* Use standard background */}
			<div className="overflow-x-hidden bg-gray-50">
				<main className="pt-24 pb-16 md:pt-32 md:pb-24">
					{" "}
					{/* Added top padding */}
					{/* --- Hero Section --- */}
					<section className="container mx-auto px-4 text-center mb-12 md:mb-16">
						<motion.div
							initial={{ scale: 0.8, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							transition={{ type: "spring", stiffness: 150, delay: 0.1 }}
							className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4"
						>
							<Gift size={36} strokeWidth={1.5} />
						</motion.div>
						<motion.h1
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.2 }}
							className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight"
						>
							Share the Style, Share the Savings!
						</motion.h1>
						<motion.p
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.3 }}
							className="text-xl md:text-2xl text-primary font-semibold mb-5"
						>
							Give 10% Off, Get Store Credit! {/* Make reward specific */}
						</motion.p>
						<motion.p
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.4 }}
							className="text-lg text-gray-600 max-w-2xl mx-auto"
						>
							Love your shades? Share Off Cut Shades with friends and you both
							get rewarded. It's simple!
						</motion.p>
					</section>
					{/* --- How It Works Section --- */}
					<section className="container mx-auto px-4 mb-12 md:mb-16 max-w-4xl mx-auto">
						<h2 className="text-2xl md:text-3xl font-semibold text-center text-gray-800 mb-8 md:mb-10">
							How It Works
						</h2>
						{/* Steps Grid */}
						<div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8 relative">
							{/* Connecting lines (visual only) - position behind cards */}
							<div
								aria-hidden="true"
								className="hidden sm:block absolute top-8 left-1/3 right-2/3 h-0.5 bg-gray-300"
							></div>
							<div
								aria-hidden="true"
								className="hidden sm:block absolute top-8 left-2/3 right-1/3 h-0.5 bg-gray-300"
							></div>

							{steps.map((step, index) => (
								<StepCard
									key={step.title}
									icon={step.icon}
									title={step.title}
									description={step.description}
									stepNumber={index + 1}
									delay={step.delay}
								/>
							))}
						</div>
						<p className="text-xs text-center text-gray-500 mt-6">
							*Store credit applied to your account after your referred friend
							completes their first purchase.
						</p>
					</section>
					{/* --- Image & CTA Section --- */}
					<section className="container mx-auto px-4">
						<div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center bg-white p-8 md:p-12 rounded-2xl shadow-lg border border-gray-200">
							{/* Content */}
							<motion.div
								className="text-center lg:text-left order-2 lg:order-1"
								initial={{ opacity: 0, x: -30 }}
								whileInView={{ opacity: 1, x: 0 }}
								viewport={{ once: true, amount: 0.3 }}
								transition={{ duration: 0.6 }}
							>
								<h2 className="text-3xl font-semibold text-gray-900 mb-4">
									Ready to Start Sharing?
								</h2>
								<p className="text-gray-600 mb-6">
									{isLoggedIn
										? "Head to your profile to find your unique referral code and start sharing!"
										: "Create a free account or log in to get your unique referral code."}
								</p>
								{isLoggedIn ? (
									<Link
										to="/profile"
										className="btn btn-primary px-7 py-3 text-base"
									>
										<Copy size={18} /> Get My Referral Code
									</Link>
								) : (
									<div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-3">
										<Link
											to="/login?redirect=/profile"
											className="btn btn-secondary-outline text-base px-6 py-2.5"
										>
											{" "}
											{/* Login & redirect */}
											Sign In
										</Link>
										<Link
											to="/register?redirect=/profile"
											className="btn btn-primary text-base px-6 py-2.5"
										>
											{" "}
											{/* Register & redirect */}
											Create Account to Refer
										</Link>
									</div>
								)}
							</motion.div>
							{/* Image */}
							<motion.div
								initial={{ opacity: 0, x: 30 }}
								whileInView={{ opacity: 1, x: 0 }}
								viewport={{ once: true, amount: 0.3 }}
								transition={{ duration: 0.6, delay: 0.1 }}
								className="order-1 lg:order-2"
							>
								<img
									src="/images/refer-friend.png" // Ensure path is correct
									alt="Friends enjoying window shades"
									className="w-full max-w-md mx-auto rounded-xl shadow-md"
									loading="lazy"
								/>
							</motion.div>
						</div>
					</section>
				</main>
			</div>
		</>
	);
};

export default ReferFriend;
